import { z } from 'zod';
import { RubricGradingType } from 'libs/end-to-end/src/types/rubrics/rubric';
import { RubricRoleOperation } from 'libs/end-to-end/src/types/rubrics/rubric';
import { RubricStatus } from 'libs/end-to-end/src/types/rubrics/rubric';

export const RubricRecordDto = z.object({
  id: z.string().uuid(),
  description: z.string(),
  organizationId: z.string().uuid(),
  name: z.string(),
  gradingType: RubricGradingType,
  integrationDataSetId: z.string().uuid(),
  status: RubricStatus,
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export type RubricRecordDto = z.infer<typeof RubricRecordDto>;

export const e: CreateRubricForWrite = {
  description: 'My Rubric is cool.',
  gradingType: 'resume',
  name: 'Cool Rubric',
  integrationDataSetId: '28e37d76-6f03-458f-b446-b4fb4c2f2cb2',
  roles: [],
  categories: [],
};

export const RubricListItemDto = z.object({
  id: z.string().uuid(),
  name: z.string(),
  roles: z.array(z.string()),
  gradingType: RubricGradingType,
  status: RubricStatus,
  createdAt: z.coerce.date(),
});

export type RubricListItemDto = z.infer<typeof RubricListItemDto>;

export const RoleRecordDto = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  name: z.string(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export type RoleRecordDto = z.infer<typeof RoleRecordDto>;

export const RoleListItemDto = RoleRecordDto.extend({
  rubricCount: z.number().int().nonnegative(),
  gradingTypes: z.array(RubricGradingType),
});

export type RoleListItemDto = z.infer<typeof RoleListItemDto>;

export const RoleForWrite = RoleRecordDto.omit({
  id: true,
  organizationId: true,
  createdAt: true,
  updatedAt: true,
});

export type RoleForWrite = z.infer<typeof RoleForWrite>;

export const CategoryRecordDto = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  gradingType: RubricGradingType,
  label: z.string(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export type CategoryRecordDto = z.infer<typeof CategoryRecordDto>;

export const CategoryForWrite = CategoryRecordDto.omit({
  id: true,
  organizationId: true,
  createdAt: true,
  updatedAt: true,
});

export type CategoryForWrite = z.infer<typeof CategoryForWrite>;

export const TagRecordDto = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  name: z.string(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export type TagRecordDto = z.infer<typeof TagRecordDto>;

export const TagForWrite = TagRecordDto.omit({
  id: true,
  organizationId: true,
  createdAt: true,
  updatedAt: true,
});

export type TagForWrite = z.infer<typeof TagForWrite>;

export const CategoryTagRecordDto = z.object({
  id: z.string().uuid(),
  categoryId: z.string().uuid(),
  tagId: z.string().uuid(),
  position: z.number().int(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export type CategoryTagRecordDto = z.infer<typeof CategoryTagRecordDto>;

export const RubricRoleRecordDto = z.object({
  id: z.string().uuid(),
  rubricId: z.string().uuid(),
  roleId: z.string().uuid(),
  roleOperation: RubricRoleOperation,
  position: z.number().int(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export type RubricRoleRecordDto = z.infer<typeof RubricRoleRecordDto>;

export const RubricCategoryRecordDto = z.object({
  id: z.string().uuid(),
  rubricId: z.string().uuid(),
  categoryId: z.string().uuid(),
  position: z.number().int(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export type RubricCategoryRecordDto = z.infer<typeof RubricCategoryRecordDto>;

export const CategoryCriteriaRecordDto = z.object({
  id: z.string().uuid(),
  categoryId: z.string().uuid(),
  criteria: z.string(),
  weight: z.number().int().min(1).max(10),
  position: z.number().int(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export type CategoryCriteriaRecordDto = z.infer<
  typeof CategoryCriteriaRecordDto
>;

export const CategoryListCriterionDto = CategoryCriteriaRecordDto.pick({
  id: true,
  criteria: true,
  weight: true,
  position: true,
});

export type CategoryListCriterionDto = z.infer<typeof CategoryListCriterionDto>;

export const CategoryListItemDto = CategoryRecordDto.extend({
  rubricCount: z.number().int().nonnegative(),
  tags: z.array(z.string()),
  criteria: z.array(CategoryListCriterionDto),
});

export type CategoryListItemDto = z.infer<typeof CategoryListItemDto>;

export const CategoryCriteriaForWrite = CategoryCriteriaRecordDto.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type CategoryCriteriaForWrite = z.infer<typeof CategoryCriteriaForWrite>;

export const CreateOrReferenceTagForWrite = z
  .object({
    name: z.string().min(1),
  })
  .strict();

export type CreateOrReferenceTagForWrite = z.infer<
  typeof CreateOrReferenceTagForWrite
>;

export const CreateOrReferenceCategoryTagForWrite = z
  .object({
    position: z.number().int(),
    tag: CreateOrReferenceTagForWrite,
  })
  .strict();

export type CreateOrReferenceCategoryTagForWrite = z.infer<
  typeof CreateOrReferenceCategoryTagForWrite
>;

export const CreateOrReferenceCategoryCriteriaForWrite = z
  .object({
    criteria: z.string(),
    weight: z.number().int().min(1).max(10),
    position: z.number().int(),
  })
  .strict();

export type CreateOrReferenceCategoryCriteriaForWrite = z.infer<
  typeof CreateOrReferenceCategoryCriteriaForWrite
>;

export const CreateOrReferenceCategoryForWrite = z.object({
  id: z.string().uuid().optional(),
  label: z.string().min(1),
  position: z.number().int(),
  categoryTags: z.array(CreateOrReferenceCategoryTagForWrite),
  categoryCriteria: z.array(CreateOrReferenceCategoryCriteriaForWrite),
});

export type CreateOrReferenceCategoryForWrite = z.infer<
  typeof CreateOrReferenceCategoryForWrite
>;

export const CreateOrReferenceRoleForWrite = z
  .object({
    name: z.string().min(1),
  })
  .strict();

export type CreateOrReferenceRoleForWrite = z.infer<
  typeof CreateOrReferenceRoleForWrite
>;

export const CreateOrReferenceRubricRoleForWrite = z.object({
  position: z.number().int(),
  roleOperation: RubricRoleOperation,
  role: CreateOrReferenceRoleForWrite,
});

export type CreateOrReferenceRubricRoleForWrite = z.infer<
  typeof CreateOrReferenceRubricRoleForWrite
>;

export const CreateRubricForWrite = RubricRecordDto.omit({
  id: true,
  organizationId: true,
  createdAt: true,
  updatedAt: true,
  status: true,
}).extend({
  roles: z.array(CreateOrReferenceRubricRoleForWrite),
  categories: z.array(CreateOrReferenceCategoryForWrite),
});

export type CreateRubricForWrite = z.infer<typeof CreateRubricForWrite>;

export const UpdateRubricForWrite = CreateRubricForWrite.omit({
  gradingType: true,
});

export type UpdateRubricForWrite = z.infer<typeof UpdateRubricForWrite>;

export const RubricDetailsCategoryDto =
  CreateOrReferenceCategoryForWrite.extend({
    id: z.string().uuid(),
  });

export type RubricDetailsCategoryDto = z.infer<typeof RubricDetailsCategoryDto>;

export const RubricIntegrationDataSetDto = z.object({
  id: z.string().uuid(),
  name: z.string(),
});

export type RubricIntegrationDataSetDto = z.infer<
  typeof RubricIntegrationDataSetDto
>;

export const RubricDetailsDto = z.object({
  id: z.string().uuid(),
  name: CreateRubricForWrite.shape.name,
  description: CreateRubricForWrite.shape.description,
  integrationDataSet: RubricIntegrationDataSetDto,
  roles: z.array(CreateOrReferenceRubricRoleForWrite),
  categories: z.array(RubricDetailsCategoryDto),
  status: RubricStatus,
  gradingType: RubricGradingType,
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export type RubricDetailsDto = z.infer<typeof RubricDetailsDto>;

export const UpdateRubricStatusBodySchema = z.object({
  status: RubricStatus,
});

export type UpdateRubricStatusBodySchema = z.infer<
  typeof UpdateRubricStatusBodySchema
>;

export const getRubricMatchingJobTitle = ({
  rubrics,
  jobTitle,
}: {
  rubrics: RubricDetailsDto[];
  jobTitle: string;
}): RubricDetailsDto | null => {
  return (
    rubrics.find((rubric) => {
      return rubric.roles.some((r) => {
        return (
          (r.roleOperation === 'isEqualTo' && r.role.name === jobTitle) ||
          (r.roleOperation === 'contains' && r.role.name.includes(jobTitle))
        );
      });
    }) ?? null
  );
};
